import { useState } from "react";

import { useBulkAddTradeTags } from "../../api/hooks/useBulkAddTradeTags";
import { useBulkAssignTradeStrategy } from "../../api/hooks/useBulkAssignTradeStrategy";
import { useBulkRemoveTradeTags } from "../../api/hooks/useBulkRemoveTradeTags";
import { useBulkReplaceTradeTags } from "../../api/hooks/useBulkReplaceTradeTags";
import { useBulkRestoreTrades } from "../../api/hooks/useBulkRestoreTrades";
import { useBulkSoftDeleteTrades } from "../../api/hooks/useBulkSoftDeleteTrades";
import { useStrategies } from "../../api/hooks/useStrategies";
import { useTags } from "../../api/hooks/useTags";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useToast } from "../ui/toast";
import { useBannerQueue } from "../shell/BannerQueue";

type BulkActionToolbarProps = {
  selectedTradeIds: string[];
  selectedDeletedCount: number;
  onClear: () => void;
};

type TagMode = "replace" | "add" | "remove";

export function BulkActionToolbar({
  selectedTradeIds,
  selectedDeletedCount,
  onClear,
}: BulkActionToolbarProps) {
  const [strategyOpen, setStrategyOpen] = useState(false);
  const [tagsOpen, setTagsOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);

  if (selectedTradeIds.length === 0) {
    return null;
  }

  const restoreVisible = selectedDeletedCount > 0;

  return (
    <div
      role="region"
      aria-label="Bulk actions"
      // Floats over the viewport bottom-center so showing/hiding the bar on
      // selection never reflows the table (the seamless-selection goal). It is
      // out of document flow, so nothing below shifts when a row is checked.
      className="fixed bottom-6 left-1/2 z-40 flex max-w-[calc(100vw-2rem)] -translate-x-1/2 flex-wrap items-center gap-3 rounded-lg border border-outline-variant bg-surface-container px-4 py-2.5 shadow-lg"
    >
      <span className="text-sm font-medium text-on-surface">
        {selectedTradeIds.length} selected
      </span>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setTagsOpen(true)}
        >
          Tags
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setStrategyOpen(true)}
        >
          Strategy
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setDeleteOpen(true)}
        >
          Delete
        </Button>
        {restoreVisible ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setRestoreOpen(true)}
          >
            Restore
          </Button>
        ) : null}
      </div>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="ml-auto"
        onClick={onClear}
      >
        Clear selection
      </Button>

      <BulkStrategyDialog
        open={strategyOpen}
        onOpenChange={setStrategyOpen}
        tradeIds={selectedTradeIds}
        onCleared={onClear}
      />
      <BulkTagsDialog
        open={tagsOpen}
        onOpenChange={setTagsOpen}
        tradeIds={selectedTradeIds}
        onCleared={onClear}
      />
      <BulkDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        tradeIds={selectedTradeIds}
        onCleared={onClear}
      />
      <BulkRestoreDialog
        open={restoreOpen}
        onOpenChange={setRestoreOpen}
        tradeIds={selectedTradeIds}
        onCleared={onClear}
      />
    </div>
  );
}

type DialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tradeIds: string[];
  onCleared: () => void;
};

function BulkStrategyDialog({ open, onOpenChange, tradeIds, onCleared }: DialogProps) {
  const { showToast } = useToast();
  const { data: strategiesData } = useStrategies();
  const strategies = (strategiesData?.items ?? []).filter((s) => !s.is_deleted);
  const [strategyId, setStrategyId] = useState<string>("");
  const assign = useBulkAssignTradeStrategy();

  async function handleSubmit() {
    try {
      await assign.mutateAsync({
        trade_ids: tradeIds,
        strategy_id: strategyId === "" ? null : strategyId,
      });
      showToast(`Updated strategy on ${tradeIds.length} trade(s).`);
      onOpenChange(false);
      onCleared();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Bulk strategy update failed.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Assign strategy to {tradeIds.length} trade(s)</DialogTitle>
        </DialogHeader>
        <label className="flex flex-col gap-1 text-sm text-on-surface">
          Strategy
          <select
            value={strategyId}
            onChange={(e) => setStrategyId(e.target.value)}
            className="rounded-md border border-outline bg-surface px-2 py-1 text-on-surface"
            aria-label="Strategy"
          >
            <option value="">(none)</option>
            {strategies.map((s) => (
              <option key={s.strategy_id} value={s.strategy_id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="button" onClick={() => void handleSubmit()} disabled={assign.isPending}>
            {assign.isPending ? "Saving…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkTagsDialog({ open, onOpenChange, tradeIds, onCleared }: DialogProps) {
  const { showToast } = useToast();
  const { data: tagsData } = useTags();
  const tags = (tagsData?.items ?? []).filter((t) => !t.is_deleted);
  const [mode, setMode] = useState<TagMode>("replace");
  const [selectedTagIds, setSelectedTagIds] = useState<Set<string>>(new Set());

  const replace = useBulkReplaceTradeTags();
  const add = useBulkAddTradeTags();
  const remove = useBulkRemoveTradeTags();
  const mutation = mode === "replace" ? replace : mode === "add" ? add : remove;

  function toggleTag(tagId: string) {
    setSelectedTagIds((prev) => {
      const next = new Set(prev);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  }

  async function handleSubmit() {
    try {
      await mutation.mutateAsync({
        trade_ids: tradeIds,
        tag_ids: Array.from(selectedTagIds),
      });
      const verb = mode === "replace" ? "Set" : mode === "add" ? "Added" : "Removed";
      showToast(`${verb} tags on ${tradeIds.length} trade(s).`);
      onOpenChange(false);
      onCleared();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Bulk tag update failed.");
    }
  }

  // Add/remove with empty tag set is a no-op on the server; disable to prevent.
  const submitDisabled =
    mutation.isPending || (mode !== "replace" && selectedTagIds.size === 0);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bulk tag {tradeIds.length} trade(s)</DialogTitle>
        </DialogHeader>
        <fieldset
          className="flex gap-3 text-sm text-on-surface"
          aria-label="Tag operation"
        >
          {(["replace", "add", "remove"] as const).map((m) => (
            <label key={m} className="flex items-center gap-2">
              <input
                type="radio"
                name="bulk-tag-mode"
                value={m}
                checked={mode === m}
                onChange={() => setMode(m)}
              />
              {m === "replace" ? "Replace" : m === "add" ? "Add" : "Remove"}
            </label>
          ))}
        </fieldset>
        {tags.length === 0 ? (
          <p className="text-sm text-on-surface-variant">No tags defined yet.</p>
        ) : (
          <ul
            aria-label="Tags"
            className="max-h-60 space-y-1 overflow-y-auto rounded-md border border-outline-variant p-2"
          >
            {tags.map((t) => (
              <li key={t.tag_id}>
                <label className="flex items-center gap-2 text-sm text-on-surface">
                  <input
                    type="checkbox"
                    checked={selectedTagIds.has(t.tag_id)}
                    onChange={() => toggleTag(t.tag_id)}
                  />
                  {t.name}
                </label>
              </li>
            ))}
          </ul>
        )}
        {mode === "replace" ? (
          <p className="text-xs text-on-surface-variant">
            Replace clears all existing tags. An empty selection removes every tag from the
            selected trades.
          </p>
        ) : null}
        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitDisabled}
          >
            {mutation.isPending ? "Saving…" : "Apply"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkDeleteDialog({ open, onOpenChange, tradeIds, onCleared }: DialogProps) {
  const { showToast } = useToast();
  const banner = useBannerQueue();
  const del = useBulkSoftDeleteTrades();
  const restore = useBulkRestoreTrades();

  async function handleConfirm() {
    const targets = tradeIds.slice();
    try {
      await del.mutateAsync({ trade_ids: targets });
      onOpenChange(false);
      onCleared();
      // M10-U3 i2: override the success-kind 5s auto-dismiss to null (sticky)
      // so operators have an unbounded window to click Undo. Manual dismiss
      // and route-change dismiss still apply (success kind defaults).
      banner.show({
        kind: "success",
        body: `Moved ${targets.length} trade(s) to the trash.`,
        autoDismissAfterMs: null,
        testId: "bulk-delete-success-banner",
        action: {
          label: "Undo",
          testId: "bulk-delete-undo",
          onClick: () => {
            void (async () => {
              try {
                await restore.mutateAsync({ trade_ids: targets });
                showToast(`Restored ${targets.length} trade(s).`);
              } catch (err) {
                showToast(err instanceof Error ? err.message : "Undo failed.");
              }
            })();
          },
        },
      });
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Bulk delete failed.");
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {tradeIds.length} trade(s)?</AlertDialogTitle>
          <AlertDialogDescription>
            Soft-deletes the selected trades. They remain visible under
            &quot;Include deleted&quot; and can be restored. The batch is atomic — if any trade
            is already deleted, none are touched.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void handleConfirm()} disabled={del.isPending}>
            {del.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function BulkRestoreDialog({ open, onOpenChange, tradeIds, onCleared }: DialogProps) {
  const { showToast } = useToast();
  const restore = useBulkRestoreTrades();

  async function handleConfirm() {
    try {
      await restore.mutateAsync({ trade_ids: tradeIds });
      showToast(`Restored ${tradeIds.length} trade(s).`);
      onOpenChange(false);
      onCleared();
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Bulk restore failed.");
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Restore {tradeIds.length} trade(s)?</AlertDialogTitle>
          <AlertDialogDescription>
            Restores the selected trades from the trash. The batch is atomic — if any trade
            is not currently soft-deleted, none are touched.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => void handleConfirm()} disabled={restore.isPending}>
            {restore.isPending ? "Restoring…" : "Restore"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
